import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Theme,
    Typography
} from "@mui/material";
import {
    FailureIcon,
    SuccessIcon
} from "../../../../assets/SvgIcons";
import LoadingButton from '@mui/lab/LoadingButton';

interface ConfirmationDialogProps {
    open: boolean;
    title?: string;
    content?: string;
    isLoading?: boolean;
    disabled?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

interface SuccessDialogProps {
    open: boolean;
    title?: string;
    content?: string;
    buttonText?: string;
    handleClick: () => void;
}

interface ErrorDialogProps {
    open: boolean;
    title?: string;
    content: string;
    handleClick: () => void
}


export function ConfirmationDialog({
    open,
    title = "Confirm Cancellation!",
    content = "Are you sure you want to continue with this action ?",
    isLoading = false,
    disabled = false,
    onCancel,
    onConfirm
}: ConfirmationDialogProps
): JSX.Element {
    return (
        <Dialog
            aria-labelledby="confirmation-dialog"
            open={open}
            PaperProps={{
                sx: (theme: Theme) => ({
                    maxWidth: '550px',
                    width: "100%",
                    borderRadius: "12px",
                    padding: "24px",
                    backgroundColor: theme.palette.common.white
                }),
            }}
        >
            <DialogTitle
                id="confirmation-dialog-title"
                sx={(theme: Theme) => ({
                    padding: "0px 0px 12px 0px",
                    ...theme.typography.h2,
                    lineHeight: "23px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary,
                })}
            >
                {title}
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: "0px",
                    overflow: "hidden"
                }}
            >
                <Typography
                    variant="subtitle_400"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.secondary
                    })}
                >
                    {content}
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: "0px",
                    marginTop: "48px",
                    columnGap: "12px"
                }}
            >
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isLoading}
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_500,
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                        borderRadius: "4px",
                        padding: "13px 56px",
                        "&:hover": {
                            border: `1px solid ${theme.palette.grey[100]}`,
                            background: theme.palette.grey[50]
                        }
                    })}
                >
                    Cancel
                </Button>
                <LoadingButton
                    fullWidth
                    loadingPosition="start"
                    variant="contained"
                    loading={isLoading}
                    onClick={onConfirm}
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_500,
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.primary[500],
                        borderRadius: "4px",
                        padding: "13px 56px",
                        boxShadow: "none",
                    })}
                >
                    Confirm
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export function SuccessDialog({
    open,
    title = "Success!",
    content = "The transaction link will be sent via email by BSE Star for authorisation and completion of the transaction.",
    buttonText = "Go to Dashboard",
    handleClick
}: SuccessDialogProps): JSX.Element {
    return (
        <Dialog
            aria-labelledby="success-dialog"
            open={open}
            PaperProps={{
                sx: (theme: Theme) => ({
                    maxWidth: '550px',
                    width: "100%",
                    borderRadius: "12px",
                    padding: "24px",
                    backgroundColor: theme.palette.common.white
                }),
            }}
        >
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    rowGap: "12px",
                    padding: "15px",
                    overflow: "hidden"
                }}
            >
                <SuccessIcon />
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    {title}
                </Typography>
                <Typography
                    variant="subtitle_400"
                    sx={(theme: Theme) => ({
                        lineHeight: "22px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.secondary,
                        textAlign: "center",
                        paddingLeft: "10px",
                        paddingRight: "10px"
                    })}
                >
                    {content}
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: "0px",
                    marginTop: "40px",
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleClick}
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_500,
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: theme.palette.common.white,
                        borderRadius: "4px",
                        padding: "13px 20px",
                        backgroundColor: theme.palette.primary[500]
                    })}
                >
                    {buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export function ErrorDialog({
    open,
    title = "Error!",
    content,
    handleClick
}: ErrorDialogProps): JSX.Element {
    return (
        <Dialog
            aria-labelledby="error-dialog"
            open={open}
            PaperProps={{
                sx: (theme: Theme) => ({
                    maxWidth: '550px',
                    width: "100%",
                    borderRadius: "12px",
                    padding: "24px",
                    backgroundColor: theme.palette.common.white
                }),
            }}
        >
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    rowGap: "12px",
                    padding: "15px",
                    overflow: "hidden"
                }}
            >
                <FailureIcon width="35" height="35" />
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    {title}
                </Typography>
                <Typography
                    variant="subtitle_400"
                    sx={(theme: Theme) => ({
                        lineHeight: "22px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.secondary,
                        textAlign: "center",
                        paddingLeft: "10px",
                        paddingRight: "10px"
                    })}
                >
                    {content}
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: "0px",
                    marginTop: "40px",
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleClick}
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_500,
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: theme.palette.common.white,
                        borderRadius: "4px",
                        padding: "13px 20px",
                        backgroundColor: theme.palette.primary[500]
                    })}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}